/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './MailingAddress.scss';

import { array, func, object, string } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import ProfileLabeledInput from 'components/layouts/Preferences/ProfileLabeledInput';
import {
  handleMultipleValidations,
  onlySpacesRegex
} from 'services/Validation';
import { getStates } from 'state/api/MetadataSelectors';
import Store from 'state/Store';
import { touchedField } from 'state/validation/ValidationActions';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import TextInput from 'components/shared/Text/TextInput';
import SelectDropdown from './SelectDropdown';
import ZipCode from './ZipCode';
import { getUpdatedFieldName, shouldFieldValidate } from 'support/AddressUtils';
import { hasResidentialAddress } from 'state/customer/CustomerSelectors';

const AddressOneWithPermissions = WithUserPermissions(TextInput);
const AddressTwoWithPermissions = WithUserPermissions(TextInput);
const AddressCityWithPermissions = WithUserPermissions(TextInput);
const AddressStateWithPermissions = WithUserPermissions(SelectDropdown);
const AddressZipCodeWithPermissions = WithUserPermissions(ZipCode);
const AddressZipCodeExtWithPermissions = WithUserPermissions(ZipCode);

const USAddress = ({
  address,
  onChange,
  states,
  invalidFields,
  validations,
  mailingType,
  hasResidential,
  touchedFields
}) => {
  const onFieldChange = (field) => (event) => {
    let value = event.target.value;
    if(onlySpacesRegex.test(value)){
      value = value.trim()
    }
    onChange(field, value);
  };

  const getFieldName = field => getUpdatedFieldName(field, hasResidential, mailingType)
  const shouldValidateField = field => shouldFieldValidate(field, touchedFields)

  const onFieldBlur = (field) => () =>
    Store.dispatch(touchedField(getFieldName(field)));


  const zipCodeValidators = [
    {
      field: 'zipCode',
      shouldValidate: shouldValidateField(getFieldName('zipCode')),
      validator: (value) => handleMultipleValidations(value, validations['zipCode']),
      value: address.zipCode
    },
    {
      field: 'zipCodeExt',
      shouldValidate: shouldValidateField(getFieldName('zipCodeExt')),
      validator: (value) => handleMultipleValidations(value, validations['zipCodeExt']),
      value: address.zipCodeExt
    }
  ];

  const addressValidator = (field) => ({
    field,
    shouldValidate: shouldValidateField(getFieldName(field)),
    validator: (value) => handleMultipleValidations(value, validations[field]),
    value: address[field]
  });

  const cityValidator = {
    field: 'city',
    shouldValidate: shouldValidateField(getFieldName('city')),
    validator: (value) => handleMultipleValidations(value, validations['city']),
    value: address.city
  };

  const stateValidator = {
    field: 'state',
    shouldValidate: shouldValidateField(getFieldName('state')),
    validator: (value) => handleMultipleValidations(value, validations['state']),
    value: address.state
  };

  return (
    <Fragment>
      <ProfileLabeledInput errorMessage={invalidFields[getFieldName('addressOne')]}
                           label="Address 1"
                           mailingType={mailingType}
                           hasResidential={hasResidential}
                           validators={[addressValidator('addressOne')]}>
        <AddressOneWithPermissions onBlur={onFieldBlur('addressOne')}
                                   onChange={onFieldChange('addressOne')}
                                   value={address.addressOne}
                                   className="address-input"
                                   dataTestId={getFieldName("addressOne")}
                                   shouldMask />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('addressTwo')]}
        label="Address 2"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[addressValidator('addressTwo')]}
      >
        <AddressTwoWithPermissions
          onBlur={onFieldBlur('addressTwo')}
          onChange={onFieldChange('addressTwo')}
          value={address.addressTwo}
          className="address-input"
          dataTestId={getFieldName("addressTwo")}
          shouldMask
        />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('city')]}
        label="City"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[cityValidator]}
      >
        <AddressCityWithPermissions
          onBlur={onFieldBlur('city')}
          onChange={onFieldChange('city')}
          value={address.city}
          className="address-input"
          dataTestId={getFieldName("city")}
          shouldMask
        />
      </ProfileLabeledInput>

      <div className="us-address-state-zip-container">
        <ProfileLabeledInput
          className="preferences-data-row-container"
          errorMessage={invalidFields[getFieldName('state')]}
          label="State"
          mailingType={mailingType}
          hasResidential={hasResidential}
          validators={[stateValidator]}
        >
          <AddressStateWithPermissions
            value={address.state}
            onBlur={onFieldBlur('state')}
            onChange={onFieldChange('state')}
            options={states}
            dataTestId={getFieldName("state")}
            shouldMask
          />
        </ProfileLabeledInput>
        <ProfileLabeledInput
          className="us-address-zip-container preferences-data-row-container"
          errorMessage={invalidFields[getFieldName('zipCode')] || invalidFields[getFieldName('zipCodeExt')]}
          label="Zip Code"
          mailingType={mailingType}
          hasResidential={hasResidential}
          validators={zipCodeValidators}
        >
          <AddressZipCodeWithPermissions
            maxLength={5}
            onBlur={onFieldBlur('zipCode')}
            onChange={onFieldChange('zipCode')}
            value={address.zipCode}
            dataTestId={getFieldName("zipCode")}
            shouldMask
          />
          <span className="address-input-zip-divider">-</span>
          <AddressZipCodeExtWithPermissions
            maxLength={4}
            onBlur={onFieldBlur('zipCodeExt')}
            onChange={onFieldChange('zipCodeExt')}
            value={address.zipCodeExt}
            dataTestId={getFieldName("zipCodeExt")}
            shouldMask
          />
        </ProfileLabeledInput>
      </div>
    </Fragment>
  );
};

USAddress.propTypes = {
  address: object.isRequired,
  onChange: func.isRequired,
  states: array.isRequired,
  invalidFields: object.isRequired,
  mailingType: string
};

const mapStateToProps = (state) => ({
  states: getStates(state),
  invalidFields: state.invalidFields,
  hasResidential: hasResidentialAddress(state),
  touchedFields:state.touchedFields
});

export default connect(mapStateToProps)(USAddress);
